import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { DocSnippetCard, SummaryCard, DemoChecklistCard } from "./DemoCard";
import DemoCard from "./DemoCard";
import heroAssets from "../data/heroAssets";

const ORANGE_HEX = "#FF8000";

const DURATION_MS = 21000; // 0–8s docs, 8–12s transition, 12–18s demo, 18–21s dwell/reset
const DWELL_COLLAPSE_MS = 700; // time to animate collapse into orb at end
// OUT-only shaping (adds linger at the orb and springier takeoff; does NOT affect IN)
const OUT_DWELL = 0.50;   // higher = longer pause at origin before launching
const OUT_BOUNCE = 2.0;   // higher = springier feel on exit
const OUT_HOLD_FRAC = 0.24;  // fraction of early progress to "hold" near the orb
const OUT_HOLD_EPS = 0.05;   // how small the held motion stays during the hold
const OUT_LAUNCH_EXP = 1.6; // >1 slows initial launch out of the orb (keep IN unchanged)
const TRANSITION_MS = 800; // faster cross-fade/swoosh for controlled prop changes

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export default function HeroAnimation({ className, scale = 1, showDemo, onPhaseChange, jumpTo, jumpToken, clusterTranslateY = 0 }: { className?: string; scale?: number; showDemo?: boolean; onPhaseChange?: (p: "docs" | "trans" | "demo" | "dwell") => void; jumpTo?: "docs" | "trans" | "demo" | "dwell"; jumpToken?: number; clusterTranslateY?: number }) {
  const rafRef = useRef<number>();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [stageSize, setStageSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [hovered, setHovered] = useState<string | null>(null);

  // Continuous loop for scrolling/typing (not tied to mode)
  const [progress, setProgress] = useState(0); // 0..1 over DURATION
  const startRef = useRef<number>(0);

  // Mode / transition
  const [currentDemo, setCurrentDemo] = useState<boolean>(!!showDemo);
  const [transitioning, setTransitioning] = useState(false);
  const [trans, setTrans] = useState(0); // 0..1
  const targetRef = useRef<boolean>(!!showDemo);
  const transStartRef = useRef<number>(0);

  useEffect(() => {
    // Trigger cross-fade when prop changes
    if (typeof showDemo === "boolean" && showDemo !== currentDemo) {
      targetRef.current = showDemo;
      setTransitioning(true);
      setTrans(0);
      transStartRef.current = performance.now();
    }
  }, [showDemo, currentDemo]);

  useEffect(() => {
    startRef.current = performance.now();
    const loop = (t: number) => {
      const p = ((t - startRef.current) % DURATION_MS) / DURATION_MS;
      setProgress(p);

      if (transitioning) {
        const tp = clamp01((t - transStartRef.current) / TRANSITION_MS);
        setTrans(tp);
        if (tp >= 1) {
          setTransitioning(false);
          setTrans(0);
          setCurrentDemo(targetRef.current);
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [transitioning]);

  // Timeline jump handler (keeps autoplay; simulates fast-forward)
  const lastJumpRef = useRef<number>(-1);
  const jumpMountedRef = useRef<boolean>(false);
  useEffect(() => {
    // Ignore the initial mount to avoid starting mid-timeline
    if (!jumpMountedRef.current) {
      jumpMountedRef.current = true;
      lastJumpRef.current = typeof jumpToken === "number" ? jumpToken : -1;
      return;
    }
    if (typeof jumpToken === "number" && jumpToken !== lastJumpRef.current) {
      lastJumpRef.current = jumpToken;
      const now = performance.now();
      switch (jumpTo) {
        case "demo":
          // Land ~1s before demo start to preserve the brief hold before demo swoosh
          startRef.current = now - 11000;
          break;
        case "trans":
          // Mid-transition
          startRef.current = now - 9000;
          break;
        case "dwell":
          // Land just after collapse completes so everything is already swooshed in
          startRef.current = now - (18000 + DWELL_COLLAPSE_MS);
          break;
        case "docs":
        default:
          startRef.current = now - 0;
          break;
      }
    }
  }, [jumpToken, jumpTo]);

  // Measure parent (orb container) to position cards relative to center
  useEffect(() => {
    const el = rootRef.current?.parentElement;
    if (!el) return;
    const compute = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, []);

  // Assets
  const docA = heroAssets.docSnippets.find((d) => d.memberId === "A")!;
  const docB = heroAssets.docSnippets.find((d) => d.memberId === "B")!;
  const sumA = heroAssets.summaries.find((s) => s.memberId === "A")!;
  const sumB = heroAssets.summaries.find((s) => s.memberId === "B")!;
  const demoA = heroAssets.demoSignals.find((d) => d.id === "demo-A" || d.id === "demo-A-add") || heroAssets.demoSignals.find(d=>d.memberId==='A' && d.kind==='add')!;
  const demoB1 = heroAssets.demoSignals.find((d) => d.memberId === "B" && d.kind === "suspect")!;
  const demoB2 = heroAssets.demoSignals.find((d) => d.memberId === "B" && d.kind === "delete")!;

  // Autoplay FSM when showDemo prop is undefined (uncontrolled)
  const autoMode = typeof showDemo !== "boolean";
  const isControlled = !autoMode;
  const tMs = progress * DURATION_MS;
  const inDocs = tMs < 8000;
  const inTrans = tMs >= 8000 && tMs < 12000;   // longer transition window (4s)
  const inDemo = tMs >= 12000 && tMs < 18000;
  const inDwell = tMs >= 18000 && tMs < 21000; // 3s pause with everything collapsed
  const dwellElapsed = inDwell ? (tMs - 18000) : 0;
  const collapseP = inDwell ? clamp01(dwellElapsed / DWELL_COLLAPSE_MS) : 0; // 0→1 during first ~700ms of dwell
  const phaseEnt = (t: number, d: number) => clamp01(t / d);
  const phaseExit = (t: number, d: number) => clamp01(t / d);

  // Highlight/slowdown schedules per doc (within docs window)
  type SlowEvent = { start: number; duration: number; highlight: string; summaryId?: string };
  const eventsA: SlowEvent[] = [
    { start: 485, duration: 900, highlight: "orthopnea 2 pillows", summaryId: "sum-A-01-s3" },
    { start: 1780, duration: 900, highlight: "A1c [8.7% (10/24)]", summaryId: "sum-A-01-s1" },
    { start: 3335, duration: 900, highlight: "UACR [280 mg/g (11/24)]", summaryId: "sum-A-01-s2" },
    { start: 5000, duration: 900, highlight: "eGFR [42]", summaryId: "sum-A-01-s2" },
    { start: 6840, duration: 900, highlight: "torsemide [20 mg qd]", summaryId: "sum-A-01-s3" },
  ];
  const eventsB: SlowEvent[] = [
    { start: 1120, duration: 800, highlight: "eGFR [43]", summaryId: "sum-B-01-s2" },
    { start: 2570, duration: 800, highlight: "Cr [1.73 mg/dL]", summaryId: "sum-B-01-s2" },
    { start: 4640, duration: 800, highlight: "UACR [190 mg/g]", summaryId: "sum-B-01-s2" },
    { start: 5900, duration: 900, highlight: "COPD exacerbation (10/24) — resolved", summaryId: "sum-B-01-s3" },
    { start: 7055, duration: 800, highlight: "COPD (stable)", summaryId: "sum-B-01-s1" },
  ];
  const DOCS_MS = 8000;
  const targetTotalPct = 1.8; // how many viewport-heights worth of scroll during docs
  const slowFactor = 0.01; // percent of fast speed during slow windows
  const clampMs = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
  const sumOverlap = (t: number, evs: SlowEvent[]) =>
    evs.reduce((acc, ev) => {
      const s = clampMs(t, 0, DOCS_MS);
      const e = Math.min(s, DOCS_MS);
      const o = Math.max(0, Math.min(ev.start + ev.duration, e) - Math.max(ev.start, 0));
      return acc + o;
    }, 0);
  const totalSlowWindow = (evs: SlowEvent[]) =>
    evs.reduce((acc, ev) => acc + Math.max(0, Math.min(ev.duration, Math.max(0, Math.min(DOCS_MS, ev.start + ev.duration) - Math.max(0, ev.start)))), 0);
  // Normalized integral of speed so that fraction reaches exactly 1 at DOCS_MS
  const docScrollFracAt = (t: number, evs: SlowEvent[]) => {
    const td = clampMs(t, 0, DOCS_MS);
    const coveredSlow = sumOverlap(td, evs);
    const totalSlow = totalSlowWindow(evs);
    const wFull = (DOCS_MS - totalSlow) * 1 + totalSlow * slowFactor;
    const wNow = (td - coveredSlow) * 1 + coveredSlow * slowFactor;
    return clamp01(wNow / Math.max(1, wFull));
  };
  const activeHighlightNow = (t: number, evs: SlowEvent[]) => {
    const ev = evs.find((e) => t >= e.start && t < e.start + e.duration);
    return ev ? [ev.highlight] : [];
  };
  const typingPctForSummary = (t: number, memberId: "A" | "B", evs: SlowEvent[]) => {
    const summary = heroAssets.summaries.find((s) => s.memberId === memberId)!;
    const sentences = summary.sentences;
    const idToIndex = new Map(sentences.map((s, i) => [s.id, i]));
    const perSentenceEvents: number[] = new Array(sentences.length).fill(0);
    const perSentenceEventCounts: number[] = new Array(sentences.length).fill(0);
    evs.forEach((ev) => {
      if (!ev.summaryId) return;
      const idx = idToIndex.get(ev.summaryId);
      if (typeof idx === "number") perSentenceEventCounts[idx] += 1;
    });
    const progressForEvent = (ev: SlowEvent) => {
      if (t < ev.start) return 0;
      if (t >= ev.start + ev.duration) return 1;
      return (t - ev.start) / ev.duration;
    };
    evs.forEach((ev) => {
      if (!ev.summaryId) return;
      const idx = idToIndex.get(ev.summaryId);
      if (typeof idx !== "number") return;
      const denom = perSentenceEventCounts[idx] || 1;
      perSentenceEvents[idx] += progressForEvent(ev) / denom;
    });
    // Cap each sentence progress at 1
    const progressBySentence = perSentenceEvents.map((p) => clamp01(p));
    const lengths = sentences.map((s) => s.text.length);
    const totalChars = lengths.reduce((a, b) => a + b, 0);
    const visibleChars = lengths.reduce((acc, len, i) => acc + Math.floor(len * progressBySentence[i]), 0);
    return totalChars === 0 ? 0 : clamp01(visibleChars / totalChars);
  };

  // Compute doc scrolling patterns
  const scrollPattern = (p: number, offset: number) => {
    const q = (p + offset) % 1;
    if (q < 0.2) return easeOutQuad(q / 0.2) * 0.3;
    if (q < 0.6) return lerp(0.3, 0.4, easeInOutCubic((q - 0.2) / 0.4));
    if (q < 0.9) return lerp(0.4, 0.5, easeOutQuad((q - 0.6) / 0.3));
    return 0.0;
  };
  // For uncontrolled autoplay during docs, use long-scroll integrator; otherwise keep gentle loop
  const scrollA = inDocs ? docScrollFracAt(tMs, eventsA) : scrollPattern(progress, 0.0);
  const scrollB = inDocs ? docScrollFracAt(tMs, eventsB) : scrollPattern(progress, 0.13);

  // Typewriter progress: add ONLY during slowdown highlights (freeze outside docs)
  let typingPctA = inDocs ? typingPctForSummary(clampMs(tMs, 0, DOCS_MS), "A", eventsA) : typingPctForSummary(DOCS_MS, "A", eventsA);
  let typingPctB = inDocs ? typingPctForSummary(clampMs(tMs, 0, DOCS_MS), "B", eventsB) : typingPctForSummary(DOCS_MS, "B", eventsB);
  // On controlled jump to demo (click), force summaries to fully typed immediately
  if (isControlled && ((transitioning && targetRef.current) || currentDemo)) {
    typingPctA = 1;
    typingPctB = 1;
  }

  // Visibility and simple entrance motion values
  let docsShow = 1, demoShow = 0;
  if (autoMode) {
    // Autoplay timeline with longer gap: docs 0–8s → trans 8–12s (hold) → demo 12–18s → dwell 18–21s
    const docsEnt = phaseEnt(Math.max(0, Math.min(tMs, 600)), 600);
    // Collapse docs quickly in first 1s of transition, then hold at orb until demo begins
    const docsExit = inTrans ? phaseExit(Math.min(tMs - 8000, 1000), 1000) : inDocs ? 0 : 1;
    // Demo progress: no pre-warm during transition; start at demo with a quick 600ms ramp then hold
    let demoProgress = 0;
    if (tMs >= 12000 && tMs < 12600) {
      demoProgress = (tMs - 12000) / 600;
    } else if (tMs >= 12600 && tMs < 18000) {
      demoProgress = 1;
    } else {
      demoProgress = 0;
    }
    docsShow = inDocs ? easeOutQuad(docsEnt) : inTrans ? 1 - easeOutQuad(docsExit) : 0;
    demoShow = easeOutQuad(demoProgress);
  } else {
    // Controlled by prop with cross-fade
    let docsOpacity = 1, demoOpacity = 0;
    if (transitioning) {
      if (targetRef.current) {
        docsOpacity = 1 - trans;
        demoOpacity = trans;
      } else {
        docsOpacity = trans;
        demoOpacity = 1 - trans;
      }
    } else {
      docsOpacity = currentDemo ? 0 : 1;
      demoOpacity = currentDemo ? 1 : 0;
    }
    docsShow = docsOpacity;
    demoShow = demoOpacity;
  }

  // Checklist state by phase
  let checklistActiveIdx: number | undefined = undefined;
  if (autoMode) {
    // 0–0.6s OCR spinner → then check
    // Docs phase: spinner on Analyzing
    // 8.0s: Identifying spinner
    // 10.0s: Compiling spinner
    // 12.0s: Compiling checked
    if (tMs < 600) {
      checklistActiveIdx = 0;
    } else if (tMs < 8000) {
      checklistActiveIdx = 1;
    } else if (tMs < 10000) {
      checklistActiveIdx = 2;
    } else if (tMs < 12000) {
      checklistActiveIdx = 3;
    } else {
      checklistActiveIdx = undefined; // all checked
    }
  } else {
    checklistActiveIdx = transitioning ? 2 : currentDemo ? undefined : 1;
  }
  const checklistItems = heroAssets.checklist.map((c, i) => {
    if (typeof checklistActiveIdx === "number") {
      // Prior steps checked
      return { label: c.label, checked: i < checklistActiveIdx };
    }
    // All checked
    return { label: c.label, checked: true };
  });

  // Hover mapping from DemoCard -> Summary
  const [activeSummaryIds, setActiveSummaryIds] = useState<string[]>([]);
  const onHoverMap = (ids?: string[]) => setActiveSummaryIds(ids || []);

  // Entrance motion factor for demo cards
  // In controlled mode, introduce a brief delay before demo cards swoosh out to mirror autoplay feel
  const CONTROLLED_DEMO_DELAY_FRAC = 0.3; // delay = 30% of TRANSITION_MS
  const ent = autoMode
    ? (inDwell ? Math.max(0, 1 - collapseP) : demoShow)
    : ((transitioning && targetRef.current)
        ? clamp01((trans - CONTROLLED_DEMO_DELAY_FRAC) / (1 - CONTROLLED_DEMO_DELAY_FRAC))
        : (currentDemo ? 1 : 0));

  // Evented highlights: choose one active highlight per doc during docs phase
  const activeHighlightsA = inDocs ? activeHighlightNow(tMs, eventsA) : [];
  const activeHighlightsB = inDocs ? activeHighlightNow(tMs, eventsB) : [];

  // Center-anchored offsets (px) derived from stage size
  const r = Math.min(stageSize.w, stageSize.h) / 2;
  // Keep cards close to the orb and symmetric; clamp for responsiveness
  const docDx = Math.max(140, Math.min(220, r - 40));
  const docDy = -40;                  // slight upward bias
  const sumDy = docDy + 200;          // summaries below docs
  const demoDyTop = 10;
  const demoDyBottom = 300;
  const demoDxLeft = Math.max(140, Math.min(220, r - 40));
  const demoDxRight = Math.max(160, Math.min(240, r - 20));

  // Origin tuning for swoosh alignment: adjust if origin appears offset from the orb center
  // Positive x => right; positive y => down
  const ORIGIN_OFFSET = { x: 0, y: 400 } // originOffset ?? { x: 0, y: 400 };
  const SHOW_ORIGIN_MARKER = false; // set true to visualize the tuned origin
  const translateFromOrigin = (tx: number, ty: number, t: number) =>
    `translate(${lerp(ORIGIN_OFFSET.x, tx, t)}px, ${lerp(ORIGIN_OFFSET.y, ty, t)}px)`;

  // Cluster pivot correction: make the swoosh aim the visual center of the doc card at the orb
  const easeOutBack = (t: number, s = 1.4) => {
    const c3 = s + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2);
  };
  const applyDwell = (t: number, dwell = 0.28) => {
    if (t <= dwell) return 0;
    return (t - dwell) / (1 - dwell);
  };
  // Size and blur shaping for a “point-in-orb” effect
  const swooshProgress = (t: number) => easeOutBack(applyDwell(t), 1.6);
  const POINT_SCALE = 0.05;     // size when at the orb (near a point)
  const OVERSHOOT_GAIN = 0.08;  // extra pop at mid-curve
  const computeScale = (t: number) => {
    const te = swooshProgress(t);
    const base = POINT_SCALE + (1 - POINT_SCALE) * te;               // 0.05 -> 1.0
    const bump = OVERSHOOT_GAIN * (te * (1 - te)) * 4;               // bell curve bump
    return Math.min(1 + OVERSHOOT_GAIN, base + bump);
  };
  const computeBlur = (t: number) => {
    const te = swooshProgress(t);
    return Math.max(0, 10 * (1 - te));
  };
  // Unified style builder: same swoosh/blur/fade for every asset given a progress p in [0..1]
  const buildSwooshStyle = (
    targetX: number,
    targetY: number,
    p: number,
    pivot: { x: number; y: number },
    withOpacity: boolean = true
  ): React.CSSProperties => {
    return {
      transform: clusterTransform(targetX, targetY, p, pivot),
      transition: "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms linear",
      filter: `blur(${computeBlur(p)}px)`,
      transformOrigin: `${pivot.x}px ${pivot.y}px`,
      ...(withOpacity ? { opacity: easeOutQuad(p) } : {}),
      willChange: "transform, opacity",
    } as React.CSSProperties;
  };
  // Explicit OUT/IN helpers (same mechanics, separate call-sites for easy tweaking later)
  const buildSwooshOutStyle = (
    targetX: number,
    targetY: number,
    pOut: number,
    pivot: { x: number; y: number },
    withOpacity: boolean = true
  ): React.CSSProperties => {
    // Pre-shape OUT progress for added linger + bounce at the orb (keeps IN untouched)
    const p0 = clamp01(pOut);
    // Hold early portion so the tiny asset is visible "loading" inside the orb
    const held = p0 < OUT_HOLD_FRAC ? (p0 / OUT_HOLD_FRAC) * OUT_HOLD_EPS : (p0 - OUT_HOLD_FRAC) / (1 - OUT_HOLD_FRAC);
    const shapedBase = easeOutBack(applyDwell(held, OUT_DWELL), OUT_BOUNCE);
    // Slow the initial launch so it doesn't "spit out" of the orb
    const shaped = Math.pow(shapedBase, OUT_LAUNCH_EXP);
    // Visibility curve: bring opacity up faster so you can see the tiny asset "loading" inside the orb
    const vis = clamp01(p0 * 3.0); // quick visibility ramp while transform lingers at orb
    return {
      transform: clusterTransform(targetX, targetY, shaped, pivot),
      transition: "transform 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms linear",
      filter: `blur(${computeBlur(vis)}px)`,
      transformOrigin: `${pivot.x}px ${pivot.y}px`,
      ...(withOpacity ? { opacity: easeOutQuad(vis) } : {}),
      willChange: "transform, opacity",
    } as React.CSSProperties;
  };

  const buildSwooshInStyle = (
    targetX: number,
    targetY: number,
    pIn: number,
    pivot: { x: number; y: number },
    withOpacity: boolean = true
  ): React.CSSProperties => {
    // Map collapse progress (0→1 to orb) to the existing path converter
    const outEquivalent = Math.max(0, 1 - pIn);
    return buildSwooshStyle(targetX, targetY, outEquivalent, pivot, withOpacity);
  };
  const docARef = useRef<HTMLDivElement | null>(null);
  const docBRef = useRef<HTMLDivElement | null>(null);
  const demoARef = useRef<HTMLDivElement | null>(null);
  const demoB1Ref = useRef<HTMLDivElement | null>(null);
  const demoB2Ref = useRef<HTMLDivElement | null>(null);
  const sumARef = useRef<HTMLDivElement | null>(null);
  const sumBRef = useRef<HTMLDivElement | null>(null);
  const checklistRef = useRef<HTMLDivElement | null>(null);
  const [pivotA, setPivotA] = useState<{ x: number; y: number }>({ x: 130, y: 90 });
  const [pivotB, setPivotB] = useState<{ x: number; y: number }>({ x: 130, y: 90 });
  const [pivotDemoA, setPivotDemoA] = useState<{ x: number; y: number }>({ x: 130, y: 90 });
  const [pivotDemoB1, setPivotDemoB1] = useState<{ x: number; y: number }>({ x: 130, y: 90 });
  const [pivotDemoB2, setPivotDemoB2] = useState<{ x: number; y: number }>({ x: 130, y: 90 });
  const [pivotSumA, setPivotSumA] = useState<{ x: number; y: number }>({ x: 90, y: 60 });
  const [pivotSumB, setPivotSumB] = useState<{ x: number; y: number }>({ x: 90, y: 60 });
  const [pivotChecklist, setPivotChecklist] = useState<{ x: number; y: number }>({ x: 110, y: 60 });
  useEffect(() => {
    const measure = () => {
      if (docARef.current) {
        const r = docARef.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotA({ x: r.width / 2, y: r.height / 2 });
      }
      if (docBRef.current) {
        const r = docBRef.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotB({ x: r.width / 2, y: r.height / 2 });
      }
      if (demoARef.current) {
        const r = demoARef.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotDemoA({ x: r.width / 2, y: r.height / 2 });
      }
      if (demoB1Ref.current) {
        const r = demoB1Ref.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotDemoB1({ x: r.width / 2, y: r.height / 2 });
      }
      if (demoB2Ref.current) {
        const r = demoB2Ref.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotDemoB2({ x: r.width / 2, y: r.height / 2 });
      }
      if (sumARef.current) {
        const r = sumARef.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotSumA({ x: r.width / 2, y: r.height / 2 });
      }
      if (sumBRef.current) {
        const r = sumBRef.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotSumB({ x: r.width / 2, y: r.height / 2 });
      }
      if (checklistRef.current) {
        const r = checklistRef.current.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) setPivotChecklist({ x: r.width / 2, y: r.height / 2 });
      }
    };
    measure();
    const roA = new ResizeObserver(measure);
    if (docARef.current) roA.observe(docARef.current);
    const roB = new ResizeObserver(measure);
    if (docBRef.current) roB.observe(docBRef.current);
    const roDA = new ResizeObserver(measure);
    if (demoARef.current) roDA.observe(demoARef.current);
    const roDB1 = new ResizeObserver(measure);
    if (demoB1Ref.current) roDB1.observe(demoB1Ref.current);
    const roDB2 = new ResizeObserver(measure);
    if (demoB2Ref.current) roDB2.observe(demoB2Ref.current);
    const roSA = new ResizeObserver(measure);
    if (sumARef.current) roSA.observe(sumARef.current);
    const roSB = new ResizeObserver(measure);
    if (sumBRef.current) roSB.observe(sumBRef.current);
    const roCL = new ResizeObserver(measure);
    if (checklistRef.current) roCL.observe(checklistRef.current);
    window.addEventListener("resize", measure);
    return () => {
      roA.disconnect();
      roB.disconnect();
      roDA.disconnect();
      roDB1.disconnect();
      roDB2.disconnect();
      roSA.disconnect();
      roSB.disconnect();
      roCL.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const clusterTransform = (targetX: number, targetY: number, t: number, pivot: { x: number; y: number }) => {
    const startX = ORIGIN_OFFSET.x - pivot.x;
    const startY = ORIGIN_OFFSET.y - pivot.y;
    const te = swooshProgress(t);
    const x = lerp(startX, targetX, te);
    const y = lerp(startY, targetY, te);
    const scale = computeScale(t);
    return `translate(${x}px, ${y}px) scale(${scale})`;
  };

  // Report phase to parent for orb frequency control
  const lastPhaseRef = useRef<string>("");
  useEffect(() => {
    let phase: "docs" | "trans" | "demo" | "dwell";
    // In controlled mode, map click state to phases so the orb follows the click
    if (isControlled) {
      phase = transitioning ? "trans" : (currentDemo ? "demo" : "docs");
    } else {
      if (inDocs) phase = "docs";
      else if (inTrans) phase = "trans";
      else if (inDemo) phase = "demo";
      else phase = "dwell";
    }
    if (onPhaseChange && lastPhaseRef.current !== phase) {
      lastPhaseRef.current = phase;
      onPhaseChange(phase);
    }
  }, [isControlled, transitioning, currentDemo, inDocs, inTrans, inDemo, inDwell, onPhaseChange]);

  // Asset positions
  const DEMO_POS_B1 = { x: -90, y: 590 };   
  const DEMO_POS_B2 = { x: 120, y: 320 };  
  const DEMO_POS_A  = { x: -380,  y: 160 }; 

  const DOC_POS_A   = { x: -360, y: 140 };
  const DOC_POS_B   = { x: 100,  y: 280 };

  const SUM_POS_B   = { x: 120,  y: 110 };
  const SUM_POS_A   = { x: -300, y: 460 };
  
  const CHECKLIST_POS = { x: -80, y: 160 };

  // Pinned assets (checklist still swooshes at start); summaries self-appear on first typing
  let pinnedProgress: number;
  if (isControlled) {
    if (transitioning) {
      // clicking: show final forms immediately when going to demo; collapse when going to docs
      pinnedProgress = targetRef.current ? 1 : Math.max(0, 1 - trans);
    } else {
      pinnedProgress = currentDemo ? 1 : docsShow;
    }
  } else {
    pinnedProgress = inDocs ? docsShow : (inDwell ? Math.max(0, 1 - collapseP) : 1);
  }
  const dwellProgress = inDwell ? Math.max(0, 1 - collapseP) : 1;
  const appearB = isControlled
    ? ((transitioning && targetRef.current) || currentDemo ? 1 : clamp01(typingPctB * 3))
    : (inDocs ? clamp01(typingPctB * 3) : 1);
  const appearA = isControlled
    ? ((transitioning && targetRef.current) || currentDemo ? 1 : clamp01(typingPctA * 3))
    : (inDocs ? clamp01(typingPctA * 3) : 1);
  const sumScaleB = inDocs ? Math.min(1.06, POINT_SCALE + (1 - POINT_SCALE) * easeOutBack(appearB, 1.6)) : 1;
  const sumScaleA = inDocs ? Math.min(1.06, POINT_SCALE + (1 - POINT_SCALE) * easeOutBack(appearA, 1.6)) : 1;
  const sumOpacityB = inDocs ? easeOutQuad(appearB) : 1;
  const sumOpacityA = inDocs ? easeOutQuad(appearA) : 1;

  return (
    <div
      ref={rootRef}
      className={clsx("pointer-events-auto select-none", className)}
      style={{ transform: `translateY(${clusterTranslateY}px) scale(${scale})` }}
    >
      {/* Center anchor */}
      <div className="absolute inset-0">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {SHOW_ORIGIN_MARKER ? (
            <div
              className="absolute pointer-events-none"
              style={{
                transform: `translate(${ORIGIN_OFFSET.x - 3}px, ${ORIGIN_OFFSET.y - 3}px)`,
                width: 6,
                height: 6,
                borderRadius: 9999,
                backgroundColor: "rgba(255,255,255,0.9)",
                boxShadow: "0 0 0 2px rgba(255,255,255,0.4)",
              }}
            />
          ) : null}
          {/* Per-member clusters: A (left) and B (right) */}
          {/* Cluster A (left side) */}
          <div
            className="absolute cursor-pointer"
            onMouseEnter={() => setHovered("docA")}
            onMouseLeave={() => setHovered(null)}
            style={
              inTrans
                ? buildSwooshInStyle(DOC_POS_A.x, DOC_POS_A.y, Math.max(0, 1 - docsShow), pivotA)
                : inDwell
                ? { ...buildSwooshInStyle(DOC_POS_A.x, DOC_POS_A.y, 1, pivotA, false), opacity: 0 }
                : buildSwooshOutStyle(DOC_POS_A.x, DOC_POS_A.y, docsShow, pivotA)
            }
          >
            {/* Doc snippet (anchored within cluster) */}
            <div
              className="absolute"
              style={{
                transform: `translate(0px, 0px)`,
                transition: "transform 160ms ease-out",
                willChange: "transform",
              }}
              ref={docARef}
            >
              <DocSnippetCard title={docA.title} subtitle={"CA · SSA 06065"} text={docA.text} activeHighlights={activeHighlightsA} scrollPct={scrollA} pulse={true} scrollRangePct={720} accent={hovered === "docA"} accentColor={ORANGE_HEX} />
            </div>
          </div>

          {/* Cluster B (right side) */}
          <div
            className="absolute cursor-pointer"
            onMouseEnter={() => setHovered("docB")}
            onMouseLeave={() => setHovered(null)}
            style={
              inTrans
                ? buildSwooshInStyle(DOC_POS_B.x, DOC_POS_B.y, Math.max(0, 1 - docsShow), pivotB)
                : inDwell
                ? { ...buildSwooshInStyle(DOC_POS_B.x, DOC_POS_B.y, 1, pivotB, false), opacity: 0 }
                : buildSwooshOutStyle(DOC_POS_B.x, DOC_POS_B.y, docsShow, pivotB)
            }
          >
            {/* Doc snippet */}
            <div
              className="absolute"
              style={{
                transform: `translate(0px, 0px)`,
                transition: "transform 160ms ease-out",
                willChange: "transform",
              }}
              ref={docBRef}
            >
              <DocSnippetCard title={docB.title} subtitle={"NY · SSA 36061"} text={docB.text} activeHighlights={activeHighlightsB} scrollPct={scrollB} pulse={true} scrollRangePct={720} accent={hovered === "docB"} accentColor={ORANGE_HEX} />
            </div>
          </div>

          {/* Demo group (container only; children control their own swoosh/opacity) */}
          <div>
            <div
              className="absolute cursor-pointer"
              onMouseEnter={() => setHovered("demoB1")}
              onMouseLeave={() => setHovered(null)}
              style={
                inDwell
                  ? buildSwooshInStyle(DEMO_POS_B1.x, DEMO_POS_B1.y, collapseP, pivotDemoB1)
                  : (transitioning && !targetRef.current)
                  ? buildSwooshInStyle(DEMO_POS_B1.x, DEMO_POS_B1.y, trans, pivotDemoB1)
                  : buildSwooshOutStyle(DEMO_POS_B1.x, DEMO_POS_B1.y, ent, pivotDemoB1)
              }
              ref={demoB1Ref}
            >
              <div style={{ transform: `translate(0px, 0px)`, transition: "transform 160ms ease-out", willChange: "transform" }}>
                <DemoCard
                  title={demoB1.title}
                  code={demoB1.code}
                  badgeLabel={(demoB1 as any).badgeLabel}
                  showActions={(demoB1 as any).showActions}
                  bottomTagText={(demoB1 as any).bottomTagText}
                  secondarySectionTitle={(demoB1 as any).secondarySectionTitle}
                  secondarySectionText={(demoB1 as any).secondarySectionText}
                  meatValidatedLetters={(demoB1 as any).meatValidatedLetters}
                  penaltyText={(demoB1 as any).penaltyText}
                  rationaleList={demoB1.rationale}
                  onRationaleHover={(id) =>
                    onHoverMap(
                      id ? (demoB1.rationale.find((r) => r.id === id)?.summaryRefs || []) : []
                    )
                  }
                  badgeIcon={(demoB1 as any).badgeIcon}
                  accent={hovered === "demoB1"}
                  accentColor={ORANGE_HEX}
                />
              </div>
            </div>
            <div
              className="absolute cursor-pointer"
              onMouseEnter={() => setHovered("demoB2")}
              onMouseLeave={() => setHovered(null)}
              style={
                inDwell
                  ? buildSwooshInStyle(DEMO_POS_B2.x, DEMO_POS_B2.y, collapseP, pivotDemoB2)
                  : (transitioning && !targetRef.current)
                  ? buildSwooshInStyle(DEMO_POS_B2.x, DEMO_POS_B2.y, trans, pivotDemoB2)
                  : buildSwooshOutStyle(DEMO_POS_B2.x, DEMO_POS_B2.y, ent, pivotDemoB2)
              }
              ref={demoB2Ref}
            >
              <div style={{ transform: `translate(0px, 0px)`, transition: "transform 160ms ease-out", willChange: "transform" }}>
                <DemoCard
                  title={demoB2.title}
                  code={demoB2.code}
                  badgeLabel={(demoB2 as any).badgeLabel}
                  badgeIcon={(demoB2 as any).badgeIcon}
                  showActions={(demoB2 as any).showActions}
                  primaryActionLabel={(demoB2 as any).primaryActionLabel}
                  secondaryActionLabel={(demoB2 as any).secondaryActionLabel}
                  penaltyText={(demoB2 as any).penaltyText}
                  bottomTagText={(demoB2 as any).bottomTagText}
                  meatValidatedLetters={(demoB2 as any).meatValidatedLetters}
                  secondarySectionTitle={(demoB2 as any).secondarySectionTitle}
                  secondarySectionText={(demoB2 as any).secondarySectionText}
                  rationaleList={demoB2.rationale}
                  onRationaleHover={(id) =>
                    onHoverMap(
                      id ? (demoB2.rationale.find((r) => r.id === id)?.summaryRefs || []) : []
                    )
                  }
                  accent={hovered === "demoB2"}
                  accentColor={ORANGE_HEX}
                />
              </div>
            </div>
            <div
              className="absolute cursor-pointer"
              onMouseEnter={() => setHovered("demoA")}
              onMouseLeave={() => setHovered(null)}
              style={
                inDwell
                  ? buildSwooshInStyle(DEMO_POS_A.x, DEMO_POS_A.y, collapseP, pivotDemoA)
                  : (transitioning && !targetRef.current)
                  ? buildSwooshInStyle(DEMO_POS_A.x, DEMO_POS_A.y, trans, pivotDemoA)
                  : buildSwooshOutStyle(DEMO_POS_A.x, DEMO_POS_A.y, ent, pivotDemoA)
              }
              ref={demoARef}
            >
              <div style={{ transform: `translate(0px, 0px)`, transition: "transform 160ms ease-out", willChange: "transform" }}>
                <DemoCard
                  title={(demoA as any).title}
                  code={(demoA as any).code}
                  badgeLabel={(demoA as any).badgeLabel}
                  badgeIcon={(demoA as any).badgeIcon}
                  showActions={(demoA as any).showActions}
                  primaryActionLabel={(demoA as any).primaryActionLabel}
                  secondaryActionLabel={(demoA as any).secondaryActionLabel}
                  penaltyText={(demoA as any).penaltyText}
                  bottomTagText={(demoA as any).bottomTagText}
                  meatValidatedLetters={(demoA as any).meatValidatedLetters}
                  secondarySectionTitle={(demoA as any).secondarySectionTitle}
                  secondarySectionText={(demoA as any).secondarySectionText}
                  rationaleList={(demoA as any).rationale}
                  onRationaleHover={(id) =>
                    onHoverMap(
                      id ? ((demoA as any).rationale.find((r: any) => r.id === id)?.summaryRefs || []) : []
                    )
                  }
                  accent={hovered === "demoA"}
                  accentColor={ORANGE_HEX}
                />
              </div>
            </div>
          </div>

          {/* Summaries: appear from themselves when typing starts; collapse during end dwell */}
          <div
            className="absolute cursor-pointer"
            onMouseEnter={() => setHovered("sumB")}
            onMouseLeave={() => setHovered(null)}
            style={{
              transform: inDwell
                ? clusterTransform(SUM_POS_B.x, SUM_POS_B.y, dwellProgress, pivotSumB)
                : `translate(${SUM_POS_B.x}px, ${SUM_POS_B.y}px) scale(${sumScaleB})`,
              transition: "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms linear",
              filter: inDwell ? `blur(${computeBlur(dwellProgress)}px)` : `blur(${computeBlur(appearB)}px)`,
              transformOrigin: `${pivotSumB.x}px ${pivotSumB.y}px`,
              opacity: inDwell ? dwellProgress : sumOpacityB,
              willChange: "transform, opacity",
            }}
            ref={sumBRef}
          >
            <div style={{ transform: `translate(0px, 0px)`, transition: "transform 160ms ease-out", willChange: "transform" }}>
              <SummaryCard title={sumB.title} sentenceList={sumB.sentences} typingPct={typingPctB} disableHighlights={true} activeSentenceIds={activeSummaryIds} accent={hovered === "sumB"} accentColor={ORANGE_HEX} />
            </div>
          </div>
          <div
            className="absolute cursor-pointer"
            onMouseEnter={() => setHovered("sumA")}
            onMouseLeave={() => setHovered(null)}
            style={{
              transform: inDwell
                ? clusterTransform(SUM_POS_A.x, SUM_POS_A.y, dwellProgress, pivotSumA)
                : `translate(${SUM_POS_A.x}px, ${SUM_POS_A.y}px) scale(${sumScaleA})`,
              transition: "transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms linear",
              filter: inDwell ? `blur(${computeBlur(dwellProgress)}px)` : `blur(${computeBlur(appearA)}px)`,
              transformOrigin: `${pivotSumA.x}px ${pivotSumA.y}px`,
              opacity: inDwell ? dwellProgress : sumOpacityA,
              willChange: "transform, opacity",
            }}
            ref={sumARef}
          >
            <div style={{ transform: `translate(0px, 0px)`, transition: "transform 160ms ease-out", willChange: "transform" }}>
              <SummaryCard title={sumA.title} sentenceList={sumA.sentences} typingPct={typingPctA} disableHighlights={true} activeSentenceIds={activeSummaryIds} accent={hovered === "sumA"} accentColor={ORANGE_HEX} />
            </div>
          </div>

          {/* Checklist near center-left (absolute so it never pushes anything) */}
          <div
            className="absolute cursor-pointer"
            onMouseEnter={() => setHovered("checklist")}
            onMouseLeave={() => setHovered(null)}
            style={{
              ...(inDwell
                ? buildSwooshInStyle(CHECKLIST_POS.x, CHECKLIST_POS.y, collapseP, pivotChecklist)
                : buildSwooshOutStyle(
                    CHECKLIST_POS.x,
                    CHECKLIST_POS.y,
                    // In controlled jump-to-demo, keep checklist present (fully out)
                    isControlled && ((transitioning && targetRef.current) || currentDemo)
                      ? 1
                      : (inDocs ? docsShow : 1),
                    pivotChecklist
                  )),
            }}
            ref={checklistRef}
          >
            <div style={{ transform: `translate(0px, 0px)`, transition: "transform 160ms ease-out", willChange: "transform" }}>
              <DemoChecklistCard activeIdx={checklistActiveIdx} items={checklistItems} accent={hovered === "checklist"} accentColor={ORANGE_HEX} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


